import { useEffect, useState } from "react";
import { Heart, PawPrint, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import DogImage from "@/components/DogImage";
import PageMeta from "@/components/PageMeta";
import { dogShopUrl } from "@/lib/dogShop";
import { SHOPIFY_URL } from "@/constants/config";

export default function Stories() {
  const [adopted, setAdopted] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dogs?status=adopted")
      .then((r) => setAdopted(r.data || []))
      .catch(() => setAdopted([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageMeta
        title="Adoption Success Stories | Lavish 808 Bulliez Rescued Paws"
        description="Read the happy endings of bulldogs rescued and rehomed by Lavish 808 Bulliez — from Hawaiʻi to Murrieta, Riverside County, and across Southern California."
      />
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Forever ʻohana</div>
          <h1 className="font-serif text-5xl md:text-7xl mb-6" style={{ color: "#0E5C6E" }}>Happy endings, new beginnings.</h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            Every adoption is a chapter closing and one just beginning. Every reunion, a reminder
            of why we do this. Mahalo to the families who opened their hearts.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center" style={{ color: "#6B655E" }}>Loading stories…</div>
          ) : adopted.length === 0 ? (
            <div className="text-center py-16">
              <PawPrint size={40} className="mx-auto mb-4 opacity-40" color="#0E5C6E" />
              <p style={{ color: "#6B655E" }} className="mb-6">Our first success stories are coming soon.</p>
              <Link to="/adopt" className="btn-primary">Meet our current pups <Heart size={14} fill="white" /></Link>
            </div>
          ) : (
            <div className="space-y-16 md:space-y-24">
              {adopted.map((d, i) => (
                <StoryBlock key={d.id} dog={d} flip={i % 2 === 1} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 text-center" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-serif text-3xl md:text-4xl mb-6" style={{ color: "#0E5C6E" }}>
            Want your pup to be next?
          </p>
          <p className="mb-8" style={{ color: "#2D2B2A" }}>
            Every adoption starts with an inquiry. Every foster gives a dog their first taste of home.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/adopt" className="btn-primary">Meet our dogs <PawPrint size={16} /></Link>
            <Link to="/get-involved" className="btn-outline">Foster or volunteer</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function StoryBlock({ dog, flip }) {
  return (
    <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 items-center`} data-testid={`story-${dog.name.toLowerCase().replace(/[^a-z]/g, '')}`}>
      <div className={`md:col-span-5 ${flip ? "md:order-2" : ""}`}>
        <div className="rounded-3xl overflow-hidden aspect-[4/5]">
          <DogImage dog={dog} />
        </div>
      </div>
      <div className="md:col-span-7 p-8 md:p-12 rounded-3xl" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="flex items-center gap-3 mb-4">
          <Heart size={22} color="#E27E4E" fill="#E27E4E" />
          <div className="text-xs uppercase tracking-[0.3em]" style={{ color: "#E27E4E" }}>Adopted • Forever ʻohana</div>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl mb-4" style={{ color: "#0E5C6E" }}>{dog.name}</h2>
        <div className="text-sm mb-6" style={{ color: "#6B655E" }}>{dog.breed} • {dog.age} • {dog.sex}</div>
        <p className="leading-relaxed mb-6 text-lg" style={{ color: "#2D2B2A" }}>{dog.story}</p>
        {dog.personality?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {dog.personality.map((p) => (
              <span key={p} className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: "white", color: "#0E5C6E" }}>{p}</span>
            ))}
          </div>
        )}
        <a
          href={dog.shop_ready ? dogShopUrl(dog.name) : SHOPIFY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium border transition-colors"
          style={{ borderColor: "#E27E4E", color: "#E27E4E", backgroundColor: "white" }}
          data-testid={`story-shop-${dog.name.toLowerCase().replace(/[^a-z]/g, "")}`}
        >
          <ShoppingBag size={16} />
          {dog.shop_ready
            ? `Shop ${dog.name.split(/[(\-]/)[0].trim()}'s Collection`
            : `Team ${dog.name.split(/[(\-]/)[0].trim()} Merch — Coming Soon`}
        </a>
      </div>
    </div>
  );
}
